/**
 * Session Resume Hook
 *
 * Handles graceful browser refresh by:
 * 1. Saving active session to localStorage
 * 2. Restoring session on app mount
 * 3. Validating session is still active on backend
 *
 * This allows users to refresh the browser without losing their work.
 */

import { useEffect } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { sessionsApi } from '../api/sessions';
import type { SessionResponse } from '../types/api';

const SESSION_STORAGE_KEY = 'facesheetapp_active_session';
const CONNECTION_STORAGE_KEY = 'facesheetapp_connection_ids';

interface StoredConnectionIds {
  customerId: string;
  practiceId: string;
  emrConnectionId: string;
}

/**
 * Save session to localStorage
 */
const saveSessionToStorage = (session: SessionResponse, connectionIds: StoredConnectionIds) => {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    localStorage.setItem(CONNECTION_STORAGE_KEY, JSON.stringify(connectionIds));
  } catch (error) {
    console.error('Failed to save session to localStorage:', error);
  }
};

/**
 * Load session from localStorage
 */
const loadSessionFromStorage = (): {
  session: SessionResponse | null;
  connectionIds: StoredConnectionIds | null;
} => {
  try {
    const sessionJson = localStorage.getItem(SESSION_STORAGE_KEY);
    const connectionJson = localStorage.getItem(CONNECTION_STORAGE_KEY);

    if (!sessionJson || !connectionJson) {
      return { session: null, connectionIds: null };
    }

    return {
      session: JSON.parse(sessionJson) as SessionResponse,
      connectionIds: JSON.parse(connectionJson) as StoredConnectionIds,
    };
  } catch (error) {
    console.error('Failed to load session from localStorage:', error);
    return { session: null, connectionIds: null };
  }
};

/**
 * Clear session from localStorage
 */
const clearSessionStorage = () => {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(CONNECTION_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear session from localStorage:', error);
  }
};

export const useSessionResume = () => {
  const { activeSession, customerId, practiceId, emrConnectionId, setActiveSession, setConnectionIds, clearSession } = useSessionStore();

  // Save session to localStorage whenever it changes
  useEffect(() => {
    if (activeSession && customerId && practiceId && emrConnectionId) {
      saveSessionToStorage(activeSession, { customerId, practiceId, emrConnectionId });
    } else if (!activeSession) {
      clearSessionStorage();
    }
  }, [activeSession, customerId, practiceId, emrConnectionId]);

  // On mount, try to restore session from localStorage
  useEffect(() => {
    const restoreSession = async () => {
      // Skip if already have active session
      if (activeSession) {
        return;
      }

      const { session, connectionIds } = loadSessionFromStorage();

      if (!session || !connectionIds) {
        return;
      }

      // Validate session is still active on backend
      try {
        const validatedSession = await sessionsApi.getSession(
          session.session_id,
          connectionIds.customerId
        );

        // Only restore if session is still Active
        if (validatedSession.session_status === 'Active') {
          setConnectionIds(
            connectionIds.customerId,
            connectionIds.practiceId,
            connectionIds.emrConnectionId
          );
          setActiveSession(validatedSession);
          console.log('Session restored from localStorage:', validatedSession.session_id);
        } else {
          // Session is no longer active, clear storage
          console.log('Stored session is no longer active, clearing');
          clearSessionStorage();
          clearSession();
        }
      } catch (error) {
        console.error('Failed to validate stored session:', error);
        // Backend error or session not found, clear storage
        clearSessionStorage();
        clearSession();
      }
    };

    restoreSession();
  }, []); // Empty dependency array - only run once on mount

  return {
    hasStoredSession: !!loadSessionFromStorage().session,
  };
};
